import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Attach admin token if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('swift_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  logout: () => localStorage.removeItem('swift_admin_token'),
};

export const public_api = {
  services: () => api.get('/services').then(r => r.data),
  fleet: () => api.get('/fleet').then(r => r.data),
  testimonials: (featured) => api.get('/testimonials', { params: { featured } }).then(r => r.data),
  content: (page) => api.get('/content', { params: page ? { page } : {} }).then(r => r.data),
  enquiry: (data) => api.post('/enquiries', data).then(r => r.data),
  booking: (data) => api.post('/bookings', data).then(r => r.data),
  quote: (data) => api.post('/quotes', data).then(r => r.data),
  referral: (data) => api.post('/referrals', data).then(r => r.data),
  newsletter: (email) => api.post('/newsletter', { email }).then(r => r.data),
  bookingRequestAccess: (email) => api.post('/bookings/request-access', { email }).then(r => r.data),
  bookingVerifyAccess: (email, code) => api.post('/bookings/verify-access', { email, code }).then(r => r.data),
  bookingLookup: (email, booking_id, token) => api.post('/bookings/lookup', { email, booking_id }, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data),
  bookingCancel: (email, booking_id, token) => api.post('/bookings/cancel', { email, booking_id }, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data),
  chat: (message, session_id) => api.post('/chat', { message, session_id }).then(r => r.data),
};

export const admin_api = {
  stats: () => api.get('/admin/stats').then(r => r.data),
  enquiries: (params) => api.get('/admin/enquiries', { params }).then(r => r.data),
  bookings: (params) => api.get('/admin/bookings', { params }).then(r => r.data),
  quotes: (params) => api.get('/admin/quotes', { params }).then(r => r.data),
  referrals: (params) => api.get('/admin/referrals', { params }).then(r => r.data),
  updateEnquiry: (id, data) => api.patch(`/admin/enquiries/${id}`, data).then(r => r.data),
  updateBooking: (id, data) => api.patch(`/admin/bookings/${id}`, data).then(r => r.data),
  updateQuote: (id, data) => api.patch(`/admin/quotes/${id}`, data).then(r => r.data),
  updateReferral: (id, data) => api.patch(`/admin/referrals/${id}`, data).then(r => r.data),
  contentList: () => api.get('/admin/content').then(r => r.data),
  contentUpsert: (key, value, page, label) =>
    api.put(`/admin/content/${encodeURIComponent(key)}`, { key, value, page, label }).then(r => r.data),
  testimonialsList: () => api.get('/admin/testimonials').then(r => r.data),
  testimonialCreate: (data) => api.post('/admin/testimonials', data).then(r => r.data),
  testimonialDelete: (id) => api.delete(`/admin/testimonials/${id}`).then(r => r.data),
  fleetList: () => api.get('/admin/fleet').then(r => r.data),
  fleetCreate: (data) => api.post('/admin/fleet', data).then(r => r.data),
  fleetUpdate: (id, data) => api.put(`/admin/fleet/${id}`, data).then(r => r.data),
  fleetDelete: (id) => api.delete(`/admin/fleet/${id}`).then(r => r.data),
  fleetMaintenance: (id, data) => api.post(`/admin/fleet/${id}/maintenance`, data).then(r => r.data),
  driversList: () => api.get('/admin/drivers').then(r => r.data),
  driverCreate: (data) => api.post('/admin/drivers', data).then(r => r.data),
  driverUpdate: (id, data) => api.put(`/admin/drivers/${id}`, data).then(r => r.data),
  driverDelete: (id) => api.delete(`/admin/drivers/${id}`).then(r => r.data),
  assignBooking: (id, data) => api.patch(`/admin/bookings/${id}/assign`, data).then(r => r.data),
  availability: (params) => api.get('/admin/availability', { params }).then(r => r.data),
  alerts: () => api.get('/admin/alerts').then(r => r.data),
  runsheet: (date) => api.get('/admin/runsheet', { params: { date } }).then(r => r.data),
  schedule: (start, end) => api.get('/admin/schedule', { params: { start, end } }).then(r => r.data),
  incidents: () => api.get('/admin/incidents').then(r => r.data),
  updateIncident: (id, data) => api.patch(`/admin/incidents/${id}`, data).then(r => r.data),
  audit: (limit) => api.get('/admin/audit', { params: { limit } }).then(r => r.data),
  exportUrl: (kind) => `${API}/admin/export/${kind}`,
};

const driverAxios = axios.create({ baseURL: API, headers: { 'Content-Type': 'application/json' } });
driverAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('swift_driver_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const driver_api = {
  login: (email, password) => driverAxios.post('/auth/login', { email, password }).then(r => r.data),
  me: () => driverAxios.get('/driver/me').then(r => r.data),
  jobs: (date) => driverAxios.get('/driver/jobs', { params: date ? { date } : {} }).then(r => r.data),
  updateJob: (id, data) => driverAxios.patch(`/driver/jobs/${id}`, data).then(r => r.data),
  reportIncident: (data) => driverAxios.post('/driver/incidents', data).then(r => r.data),
  logout: () => localStorage.removeItem('swift_driver_token'),
};
